import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import { ArrowLeftRight, Plus, Dash } from 'react-bootstrap-icons'

const OutletStockTransfer = () => {

    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    
    const accessToken = localStorage.getItem('authToken');
    const animatedComponents = makeAnimated();
    const [outlets, setOutlets] = useState([]);
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [inputData, setInputData] = useState({
        outletId: "",
        outletStockTransferData: [{
        rawMaterialId: "",
        currentStockQuantity: "",
        transferQuantity: "",
        storageLocation: "",
        unit: ""
        }]
    });

    const [filteredStorageLocations, setFilteredStorageLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        outletId: "",
        outletStockTransferData: [{
        rawMaterialId: "",
        currentStockQuantity: "",
        transferQuantity: "",
        storageLocation: ""
        }]
    });

    // Fetch all necessary data on component mount
    useEffect(() => {
        getOutlets();
        getRawMaterials();
    }, []);

    const getOutlets = async () => {
        try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const response = await axios.get("outlet", { headers });
        if (response?.data?.status === true) {
            setOutlets(response?.data?.data?.outlets);
        }
        } catch (error) {
        handleApiError(error);
        }
    };

    const getRawMaterials = async () => {
        try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const response = await axios.get("raw-material", { headers });
        if (response?.data?.status === true) {
            setRawMaterialList(response?.data?.data?.rawMaterials);
        }
        } catch (error) {
        handleApiError(error);
        }
    };

    const getStorageLocation = async (rawId, storageId, index) => {
        try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const response = await axios.get(`storage-location/getCurrentStock?rawMaterialId=${rawId}`, { headers });
        const storageLocations = response?.data?.data?.storageLocations;

        setFilteredStorageLocations(storageLocations);
        
        if (storageId) {
            const storageLoc = storageLocations?.find(st => st?._id === storageId);
            const filteredStock = storageLoc?.stockInfo?.find(cs => cs?.rawMaterial?._id === rawId);
            
            // Update the inputData with current stock and unit
            setInputData(prevData => {
            const newData = { ...prevData };
            newData.outletStockTransferData = [...newData.outletStockTransferData];
            newData.outletStockTransferData[index] = {
                ...newData.outletStockTransferData[index],
                currentStockQuantity: filteredStock?.currentStock || 0,
                unit: filteredStock?.rawMaterial?.unit || "",
                storageLocation: storageId
            };
            return newData;
            });
        }
        } catch (error) {
        handleApiError(error);
        }
    };

    const handleApiError = (error) => {
        if (error.response && error.response?.data?.status === false) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
        });
        }
    };

    const handleAddItem = () => {
        // Validate current items before adding new one
        const stockValuesErrors = inputData.outletStockTransferData.map(validateOutletStockTransferDataItem);
        if (stockValuesErrors.some(errors => Object.keys(errors).length > 0)) {
        setValidationErrors(prevErrors => ({
            ...prevErrors,
            outletStockTransferData: stockValuesErrors,
        }));
        return;
        }

        setInputData(prevData => ({
        ...prevData,
        outletStockTransferData: [
            ...prevData.outletStockTransferData,
            {
            rawMaterialId: "",
            currentStockQuantity: "",
            transferQuantity: "",
            storageLocation: "",
            unit: ""
            }
        ]
        }));
    };

    const validateOutletStockTransferDataItem = (item) => {
        const errors = {};
        if (!item.rawMaterialId) errors.rawMaterialId = "Raw Material is required";
        if (!item.storageLocation) errors.storageLocation = "Storage Location is required";
        if (item.currentStockQuantity === "") errors.currentStockQuantity = "Current Stock is required";
        if (item.transferQuantity === "") errors.transferQuantity = "Transfer Qty is required";
        else if (parseFloat(item.transferQuantity) > parseFloat(item.currentStockQuantity)) {
        errors.transferQuantity = "Cannot transfer more than current stock";
        }
        return errors;
    };

    const handleRemoveItem = (index) => {
        setInputData(prevData => {
        const updatedItems = [...prevData.outletStockTransferData];
        updatedItems.splice(index, 1);
        return { ...prevData, outletStockTransferData: updatedItems };
        });
    };

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        
        // Clear validation errors when changing
        setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (name === "outletId") newErrors.outletId = "";
        
        newErrors.outletStockTransferData = newErrors.outletStockTransferData.map((err, i) => 
            i === index ? { ...err, [name]: "" } : err
        );
        return newErrors;
        });

        if (["rawMaterialId", "currentStockQuantity", "transferQuantity", "storageLocation"].includes(name)) {
        setInputData(prevData => {
            const updatedData = { ...prevData };
            updatedData.outletStockTransferData = [...updatedData.outletStockTransferData];
            updatedData.outletStockTransferData[index] = {
            ...updatedData.outletStockTransferData[index],
            [name]: name === "transferQuantity" ? parseFloat(value) || 0 : value
            };
            return updatedData;
        });
        } else {
        setInputData(prevData => ({ ...prevData, [name]: value }));
        }
    };

    const handleRawMaterialSelect = (selectedOption, index) => {
        if (selectedOption) {
        const rawMaterialId = selectedOption.value;
        
        setInputData(prevData => {
            const updatedData = { ...prevData };
            updatedData.outletStockTransferData = [...updatedData.outletStockTransferData];
            updatedData.outletStockTransferData[index] = {
            ...updatedData.outletStockTransferData[index],
            rawMaterialId,
            transferQuantity: 0,
            currentStockQuantity: 0,
            storageLocation: "",
            unit: ""
            };
            return updatedData;
        });

        // Fetch storage locations for this raw material
        getStorageLocation(rawMaterialId, "", index);
        } else {
        // Clear all fields if selection is cleared
        setInputData(prevData => {
            const updatedData = { ...prevData };
            updatedData.outletStockTransferData = [...updatedData.outletStockTransferData];
            updatedData.outletStockTransferData[index] = {
            rawMaterialId: "",
            currentStockQuantity: 0,
            unit: "",
            transferQuantity: 0,
            storageLocation: ""
            };
            return updatedData;
        });
        }
    };

    const handleStorageLocationSelect = (selectedOption, index) => {
        const storageId = selectedOption?.value || "";
        const rawMaterialId = inputData.outletStockTransferData[index].rawMaterialId;

        setInputData(prevData => {
        const updatedData = { ...prevData };
        updatedData.outletStockTransferData = [...updatedData.outletStockTransferData];
        updatedData.outletStockTransferData[index] = {
            ...updatedData.outletStockTransferData[index],
            storageLocation: storageId
        };
        return updatedData;
        });

        if (storageId && rawMaterialId) {
        getStorageLocation(rawMaterialId, storageId, index);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Validate all fields
        const outletStockTransferDataErrors = inputData.outletStockTransferData.map(
        validateOutletStockTransferDataItem
        );

        const hasErrors = outletStockTransferDataErrors.some(
        errors => Object.keys(errors).length > 0
        ) || !inputData.outletId;

        if (hasErrors) {
        setValidationErrors({
            outletId: !inputData.outletId ? "Outlet is required" : "",
            outletStockTransferData: outletStockTransferDataErrors
        });
        setIsLoading(false);
        return;
        }

        try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const response = await axios.post(
            "stock-transactions/addStockToOutlet",
            inputData,
            { headers }
        );

        if (response?.data?.status === true) {
            toast.success(response?.data?.data?.message, {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            });
            // Reset form
            setInputData({
            outletId: "",
            outletStockTransferData: [{
                rawMaterialId: "",
                currentStockQuantity: "",
                transferQuantity: "",
                storageLocation: "",
                unit: ""
            }]
            });
        }
        } catch (error) {
        if (error?.response) {
            let errorMessage = '';
            if (error.response?.status === 406) {
            errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
            } else if (error.response?.status === 412) {
            errorMessage = error?.response?.data?.error;
            } else if (error.response?.status === 500) {
            errorMessage = error?.response?.data?.error;
            }

            toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            });
        }
        } finally {
        setIsLoading(false);
        }
    };

    const isFormValid = () => {
        if (!inputData.outletId) return false;
        
        return inputData.outletStockTransferData.every(item => 
        item.rawMaterialId && 
        item.storageLocation && 
        item.currentStockQuantity !== "" && 
        item.transferQuantity !== "" &&
        parseFloat(item.transferQuantity) <= parseFloat(item.currentStockQuantity)
        );
    };


    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <Card className="shadow-sm">
                    <Card.Header className="bg-primary text-white d-flex align-items-center p-3">
                        <ArrowLeftRight className="me-2 fs-4" />
                        STOCK TRANSFER TO OUTLET
                    </Card.Header>
                    
                    <Form onSubmit={handleSubmit}>
                    <Card.Body>
                        <Row className="mb-4">
                        <Col md={6}>
                            <Form.Group controlId="outletSelect">
                            <Form.Label className="fw-semibold">
                                Select Outlet <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                                options={outlets.map(outlet => ({
                                value: outlet._id,
                                label: outlet.name
                                }))}
                                value={inputData.outletId ? {
                                value: inputData.outletId,
                                label: outlets.find(o => o._id === inputData.outletId)?.name
                                } : null}
                                onChange={(selectedOption) => {
                                setInputData({
                                    ...inputData,
                                    outletId: selectedOption?.value || ""
                                });
                                setValidationErrors({
                                    ...validationErrors,
                                    outletId: ""
                                });
                                }}
                                placeholder="Select Outlet"
                                isClearable
                                classNamePrefix="react-select"
                            />
                            {validationErrors.outletId && (
                                <Form.Text className="text-danger">
                                {validationErrors.outletId}
                                </Form.Text>
                            )}
                            </Form.Group>
                        </Col>
                        </Row>

                        {inputData.outletId && (
                        <>
                            <div className="mb-3">
                            <Badge bg="info" className="fs-6">
                                Transfer Items
                            </Badge>
                            </div>

                            {inputData.outletStockTransferData.map((item, index) => (
                            <Card key={index} className="mb-3 border">
                                <Card.Body>
                                <Row className="g-3 align-items-center">
                                    <Col md={5}>
                                    <Form.Group controlId={`rawMaterial-${index}`}>
                                        <Form.Label className="fw-semibold">
                                        Raw Material <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Select
                                        components={animatedComponents}
                                        options={rawMaterialList.map(rm => ({
                                            value: rm._id,
                                            label: rm.name,
                                            isDisabled: inputData.outletStockTransferData.some(
                                            (data, i) => i !== index && data.rawMaterialId === rm._id
                                            )
                                        }))}
                                        value={item.rawMaterialId ? {
                                            value: item.rawMaterialId,
                                            label: rawMaterialList.find(
                                            rm => rm._id === item.rawMaterialId
                                            )?.name
                                        } : null}
                                        onChange={(selectedOption) => handleRawMaterialSelect(selectedOption, index)}
                                        placeholder="Select Raw Material"
                                        isClearable
                                        classNamePrefix="react-select"
                                        />
                                        {validationErrors.outletStockTransferData[index]?.rawMaterialId && (
                                        <Form.Text className="text-danger">
                                            {validationErrors.outletStockTransferData[index]?.rawMaterialId}
                                        </Form.Text>
                                        )}
                                    </Form.Group>
                                    </Col>

                                    <Col md={5}>
                                    <Form.Group controlId={`storageLocation-${index}`}>
                                        <Form.Label className="fw-semibold">
                                        Storage Location <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Select
                                        components={animatedComponents}
                                        value={item.storageLocation ? {
                                            value: item.storageLocation,
                                            label: filteredStorageLocations.find(
                                            sl => sl._id === item.storageLocation
                                            )?.name
                                        } : null}
                                        onChange={(selectedOption) => handleStorageLocationSelect(selectedOption, index)}
                                        options={filteredStorageLocations.map(sl => ({
                                            value: sl._id,
                                            label: `${sl.name} (${sl.city}, ${sl.state})`
                                        }))}
                                        placeholder="Select Storage Location"
                                        isClearable
                                        isDisabled={!item.rawMaterialId}
                                        classNamePrefix="react-select"
                                        />
                                        {validationErrors.outletStockTransferData[index]?.storageLocation && (
                                        <Form.Text className="text-danger">
                                            {validationErrors.outletStockTransferData[index]?.storageLocation}
                                        </Form.Text>
                                        )}
                                    </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                    <Form.Group controlId={`currentStock-${index}`}>
                                        <Form.Label className="fw-semibold">Current Stock</Form.Label>
                                        <Form.Control
                                        type="text"
                                        readOnly
                                        value={`${item.currentStockQuantity || 0} ${item.unit || ''}`}
                                        />
                                    </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                    <Form.Group controlId={`transferQty-${index}`}>
                                        <Form.Label className="fw-semibold">
                                            Transfer Qty <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={item.transferQuantity || ""}
                                            onChange={(e) => handleChange({
                                                target: {
                                                name: "transferQuantity",
                                                value: e.target.value
                                                }
                                            }, index)}
                                            disabled={!item.storageLocation}
                                        />
                                        {validationErrors.outletStockTransferData[index]?.transferQuantity && (
                                        <Form.Text className="text-danger">
                                            {validationErrors.outletStockTransferData[index]?.transferQuantity}
                                        </Form.Text>
                                        )}
                                    </Form.Group>
                                    </Col>

                                    <Col md={1} className="d-flex align-items-end">
                                    {index === inputData.outletStockTransferData.length - 1 ? (
                                        <Button
                                        variant="outline-primary"
                                        onClick={handleAddItem}
                                        className="w-100"
                                        title="Add another item"
                                        >
                                        <Plus />
                                        </Button>
                                    ) : (
                                        <Button
                                        variant="outline-danger"
                                        onClick={() => handleRemoveItem(index)}
                                        className="w-100"
                                        title="Remove this item"
                                        >
                                        <Dash />
                                        </Button>
                                    )}
                                    </Col>
                                </Row>
                                </Card.Body>
                            </Card>
                            ))}
                        </>
                        )}
                    </Card.Body>

                    <Card.Footer className="text-end">
                        <Button
                        variant="primary"
                        type="submit"
                        disabled={!isFormValid() || isLoading}
                        >
                        {isLoading ? (
                            <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Processing...
                            </>
                        ) : (
                            "Transfer Stock"
                        )}
                        </Button>
                    </Card.Footer>
                    </Form>
                </Card>
            </div>
        </React.Fragment>
    )
}

export default OutletStockTransfer